import { DateTime } from 'luxon';
import { CashSessionStatus } from '@domains/treasury/util';
import type { TreasuryCashTurnLoadRaw } from '../infra/treasury-cash-turn-context.service';

export type TreasuryMoney = number | null;
export type TreasuryStatusTone =
  | 'positive'
  | 'negative'
  | 'neutral'
  | 'warning'
  | 'info';

export interface TreasuryOptionVm<TValue = string> {
  value: TValue;
  label: string;
}

/** Backend enum for `POST /CashOperation/CreateOperation` — `OperationType` (query string). */
export enum CashOperationType {
  Supply = 0,
  Withdrawal = 1,
  Adjustment = 2,
}

/** API `FinancialAccount.Type` — numeric enum from backend. */
export enum FinancialAccountType {
  Cash = 0,
  Bank = 1,
  Wallet = 2,
  Investment = 3,
}

/** API `CashFlowEntry.Direction` / `CashFlowReportLine.Direction`. */
export enum FlowDirection {
  In = 0,
  Out = 1,
}

/**
 * Wire-fidelity enum for `CashFlowEntries.EntryType` as returned by OData
 * (both string names via `StringEnumConverter` and raw ints via the
 * `$apply=groupby` aggregate path). Keeps Sangria (Withdrawal) and
 * Suprimento (Supply) distinct from a generic Adjustment so consumers can
 * filter and label them independently.
 *
 * Supply / Withdrawal / Adjustment all behave as cash-drawer adjustments
 * (no payment-method settlement); compare via the {@link isAdjustmentEntryType}
 * helper when you want any of the three.
 */
export enum BackendEntryType {
  Sale = 0,
  Expense = 1,
  Supply = 2,
  Withdrawal = 3,
  Transfer = 4,
  Adjustment = 5,
}

/**
 * True for the three entry types that the UI treats as "adjustments"
 * (drawer suprimento / sangria / ajuste manual). Centralized here so the
 * timeline, print service and aggregate store don't drift on which wire
 * values count as adjustment-class.
 */
export const isAdjustmentEntryType = (value: BackendEntryType): boolean =>
  value === BackendEntryType.Supply ||
  value === BackendEntryType.Withdrawal ||
  value === BackendEntryType.Adjustment;

/**
 * API `CashFlowEntry.Status`.
 * Inference pending backend confirmation: fixtures emit `0` on creation;
 * the service exposes `ConfirmEntry` and `CancelEntry` endpoints.
 */
export enum CashFlowEntryStatus {
  Draft = 0,
  Confirmed = 1,
  Cancelled = 2,
}

/** API `CashFlowEntry.ReconciliationStatus`. */
export enum ReconciliationStatus {
  Reconciled = 0,
  Pending = 1,
  Unmatched = 2,
  Exception = 3,
}

export interface TreasuryValidationError {
  field?: string;
  message?: string;
}

export interface TreasuryValidationResult {
  code?: number | string | null;
  message?: string | null;
  errors?: TreasuryValidationError[] | null;
}

export interface TreasuryBankConnectionViewModel {
  bankConnectionId?: string | null;
  connectionId?: string | null;
  providerId?: string | null;
  accountId?: string | null;
  bankName?: string | null;
  status?: string | null;
  [key: string]: unknown;
}

/** API contract: PascalCase (server DTOs). */
export interface FinancialAccount {
  FinancialAccountId: string;
  Name: string;
  Type: FinancialAccountType;
  CurrentBalance: TreasuryMoney;
  InitialBalance: TreasuryMoney;
  AllowNegative: boolean;
  Active: boolean;
  /**
   * Marks the single cash account used for **Caixa Simplificado** mode —
   * every payment method (cash/card/pix/bankslip) settles into it. Set by the
   * backend on the account configured in treasury-settings while
   * `AutoSessionEnabled` is true.
   */
  IsSimpleCashSession?: boolean;
  LastMovementAt?: string | null;
  BankConnection?: TreasuryBankConnectionViewModel | null;
  validationResult?: TreasuryValidationResult | null;
  isValid?: boolean;
}

export type TFinancialAccount = FinancialAccount;

/** API contract: PascalCase (server DTOs). */
export interface CashSession {
  CashSessionId: string;
  FinancialAccountId: string;
  FinancialAccountName?: string | null;
  /**
   * Mirrors {@link FinancialAccount.IsSimpleCashSession}. The backend snapshots
   * the flag on the session at open-time so the UI can decide cash-vs-conference
   * presentation (timeline hint, muted row styling, signed amounts) from the
   * session DTO alone — no extra round-trip to treasury-settings.
   */
  IsSimpleCashSession?: boolean;
  OpenedBy: string;
  /**
   * When present on GET `/CashSession` (and sometimes list responses), display name for
   * {@link OpenedBy} so the UI does not depend only on `personNameById` resolution.
   */
  OpenedByName?: string | null;
  /** Alternate display field from some API payloads (full name). */
  OpenedByFullName?: string | null;
  ClosedBy?: string | null;
  OpeningBalance: TreasuryMoney;
  ClosingBalance?: TreasuryMoney;
  ExpectedBalance?: TreasuryMoney;
  ExpectedCashBalance?: TreasuryMoney;
  ExpectedCardBalance?: TreasuryMoney;
  ExpectedPixBalance?: TreasuryMoney;
  ExpectedBankSlipBalance?: TreasuryMoney;
  ExpectedCheckBalance?: TreasuryMoney;
  Difference?: TreasuryMoney;
  Status: CashSessionStatus;
  OpenedOnUtc: string;
  ClosedOnUtc?: string | null;
  notes?: string | null;
  validationResult?: TreasuryValidationResult | null;
  isValid?: boolean;
}

/**
 * API `CashFlowEntry.PaymentMethod` — numeric enum returned by the
 * treasury backend. Mirrors the receipt breakdown (Cash/Card/Pix/
 * BankSlip/Check) shown on the movement detail drawer.
 */
export enum CashFlowPaymentMethod {
  Cash = 0,
  Card = 1,
  Pix = 2,
  BankSlip = 3,
  Check = 4,
}

/** API contract: PascalCase (server DTOs). */
export interface CashFlowEntry {
  CashFlowEntryId: string;
  FinancialAccountId: string;
  CashSessionId?: string | null;
  Direction: FlowDirection;
  /**
   * Wire enum {@link BackendEntryType} — Sale / Expense / Supply
   * (Suprimento) / Withdrawal (Sangria) / Transfer / Adjustment.
   */
  EntryType: BackendEntryType;
  Amount: TreasuryMoney;
  Description: string;
  CompetenceDate: string;
  PaymentDate?: string | null;
  Status: CashFlowEntryStatus;
  Category?: string | null;
  PaymentMethod?: CashFlowPaymentMethod | null;
  ReconciliationStatus?: ReconciliationStatus | null;
  MatchedBankTransactionId?: string | null;
  MatchConfidence?: number | string | null;
  MatchMethod?: string | null;
  originId?: string | null;
  originType?: number | string | null;
  reconciledTransactionId?: string | null;
  /** Server-stamped audit timestamp from `/OData/CashFlowEntries` (full datetime). */
  AddedOnUtc?: string | null;
  /** Line-item / product description, denormalized from the originating request. */
  Details?: string | null;
  CashAmount?: number | null;
  CardAmount?: number | null;
  PixAmount?: number | null;
  BankSlipAmount?: number | null;
  CheckAmount?: number | null;
  /** Present when the entry was created via a sale/receipt request — gates the payment-breakdown panel. */
  CashFlowEntryRequestId?: string | null;
  RequestDescription?: string | null;
  /** Card / acquirer transaction id (`TId` on OData `CashFlowEntries`). */
  TId?: string | null;
  CardholderName?: string | null;
  CardNumber?: string | null;
  CardBrand?: string | null;
  /** Customer of the originating request (sale/receipt). FullName resolved separately via PersonLookupService. */
  PersonId?: string | null;
  /** Audit PersonId of the user who recorded the entry. FullName resolved via PersonLookupService → `personNameById`. */
  AddedBy?: string | null;
  /**
   * Chart-of-accounts NodeId linked to this entry — feeds the DRE
   * Gerencial (gates the per-leaf-row DRE drill-down, US7). Entries
   * without this link (coming from the queue) stay out of the DRE.
   */
  ChartOfAccountsNodeId?: string | null;
  validationResult?: TreasuryValidationResult | null;
  isValid?: boolean;
}

/**
 * One row of the OData `$apply=groupby((CompetenceDate,Direction,EntryType,FinancialAccountId),aggregate(Amount with sum as Total,$count as Count))`
 * response, decoded back into TS enums. Used to power the 7-day sparkline on the daily summary cards.
 *
 * `FinancialAccountId` is part of the groupby tuple so consumers can build a
 * per-day-per-account breakdown (tooltip composition) without re-querying.
 * The sparkline line itself sums across accounts; the per-account slice is
 * additive context.
 */
export interface TenantDailySeriesPoint {
  /** Financial competence day (`Edm.Date`, `yyyy-MM-dd`); consumers truncate to date-only when bucketizing per day. */
  CompetenceDate: string;
  Direction: FlowDirection;
  EntryType: BackendEntryType;
  /** Originating financial account for this aggregated group. */
  FinancialAccountId: string;
  /** Sum of Amount for this (day, direction, entryType, account) group. */
  Total: number;
  /** Number of rows aggregated into this group. */
  Count: number;
}

/**
 * One row of the OData
 * `$apply=filter(...)/groupby((Direction,EntryType,PaymentMethod,FinancialAccountId),aggregate(Amount with sum as Total,$count as Count))`
 * response, decoded back into TS enums. Powers the home "today" cards:
 * `dailyTotals` collapses across PaymentMethod + FinancialAccountId,
 * `dailyTotalsByAccount` collapses across PaymentMethod, and
 * `dailyByPaymentMethod` collapses across EntryType + FinancialAccountId.
 *
 * `PaymentMethod` is nullable because adjustments / system-generated rows
 * may have no payment method on file.
 */
export interface TenantDailyAggregatePoint {
  Direction: FlowDirection;
  EntryType: BackendEntryType;
  PaymentMethod: CashFlowPaymentMethod | null;
  /** Originating financial account for this aggregated group. */
  FinancialAccountId: string;
  /** Sum of Amount for this (direction, entryType, method, account) group. */
  Total: number;
  /** Number of rows aggregated into this group. */
  Count: number;
}

export interface CashOperation {
  CashOperationId: string;
  CashSessionId: string;
  CashFlowEntryId?: string | null;
  OperationType: CashOperationType;
  Amount: TreasuryMoney;
  Reason: string;
  AuthorizedBy: string;
  OperatedOn?: string | null;
  validationResult?: TreasuryValidationResult | null;
  isValid?: boolean;
}

export interface CashTransfer {
  CashTransferId?: string;
  FromAccountId: string;
  ToAccountId: string;
  Amount: TreasuryMoney;
  TransferDate: string;
  Description?: string | null;
  OriginEntryId?: string;
  DestEntryId?: string;
  validationResult?: TreasuryValidationResult | null;
  isValid?: boolean;
}

export interface TreasuryFinancialAccountFormValue {
  FinancialAccountId: string | null;
  Name: string | null;
  Type: FinancialAccountType | null;
  InitialBalance: TreasuryMoney;
  AllowNegative: boolean;
  Active: boolean;
}

export interface TreasuryAccountTransferFormValue {
  FromAccountId: string;
  ToAccountId: string;
  /** Bound to `lib-input` currency CVA (string) or numeric fallback. */
  Amount: number | string;
  TransferDate: string;
  Description: string;
}

export interface TreasuryStoreState {
  cashHomeChartsRows: CashHomeChartsAggregateRow[];
  lastUpdatedAt: string | null;
  dateRange: TreasuryDateRange;
  financialAccounts: FinancialAccount[];
  /** Sessions with `Status = Open`. Populated by `loadHomeCashSessions`. */
  openSessions: CashSession[];
  /** Sessions with `Status` in (`Closing`, `PendingReview`). Populated by `loadHomeCashSessions`. */
  pendingSessions: CashSession[];
  /** Operator's own sessions opened within the recent window (any status). Populated by `loadHomeCashSessions`. */
  myRecentSessions: CashSession[];
  cashFlowReport: CashFlowReportSnapshot | null;
  tenantDailyAggregates: TenantDailyAggregatePoint[] | null;
  /** Aggregated 7-day sparkline points (one per (day, direction, entryType) group). */
  tenantDailySeries: TenantDailySeriesPoint[];
  /**
   * Window requested for `tenantDailySeries`. Computed `dailyTotalsDailySeries` uses
   * this to build a fixed-length zero-filled array (days with no rows must still appear).
   */
  tenantDailySeriesRange: TreasuryDateRange | null;
  reconciliationDashboard: TreasuryDashboardPayload | null;
  /** Map of PersonId → FullName for resolving session operator names. */
  personNameById: Map<string, string>;
  /** Current user's open cash sessions from GET `/CashSession/GetMyOpenSession` (for receipts / bill-to-receive). */
  myOpenSessions: CashSession[];
  /**
   * Entry counts per open cash session from OData `CashFlowEntries` (`$count` + `$top=0`).
   * Used for home “Caixas em operação” movement totals (report lines are not loaded there).
   */
  sessionCashFlowEntryCountById: Record<string, number>;
  /**
   * Latest summary aggregates for the account-detail cards
   * (`totalIn`/`totalOut`/`totalReconciled`). Refreshed alongside the grid's
   * server-side DataSource — see {@link CashFlowAggregates}. Request-state
   * (loading/error) lives on `loadCashFlowAggregatesSWTState` via
   * `withReqState`, not on this slot.
   */
  cashFlowAggregates: CashFlowAggregates | null;
  /**
   * Running net balance per local day (keyed by the day's last `AddedOnUtc`).
   * Populated by `loadCashFlowEntriesByDay`; see {@link CashFlowEntriesByDayMap}.
   */
  cashFlowEntriesByDay: CashFlowEntriesByDayMap | null;
  /** Cached cash-flow entries keyed by `CashSessionId`. */
  sessionCashFlowEntries: Record<string, CashFlowEntry[]>;
  /** Turn context loaded via loadCashTurnContext. */
  turnContextLoadRaw: TreasuryCashTurnLoadRaw | null;
}

export interface TreasuryFinancialAccountState {
  account: FinancialAccount | null;
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  error: string | null;
  saveError: string | null;
  deleteError: string | null;
  saveSuccess: boolean;
  deleteSuccess: boolean;
  lastAction: 'create' | 'update' | 'delete' | null;
  lastSavedAccountId: string | null;
}

export const initialTreasuryStoreState: TreasuryStoreState = {
  cashHomeChartsRows: [],
  lastUpdatedAt: null,
  dateRange: {
    StartDate: DateTime.local().toISODate(),
    EndDate: DateTime.local().toISODate(),
  },
  financialAccounts: [],
  openSessions: [],
  pendingSessions: [],
  myRecentSessions: [],
  cashFlowReport: null,
  cashFlowAggregates: null,
  cashFlowEntriesByDay: null,
  tenantDailyAggregates: null,
  tenantDailySeries: [],
  tenantDailySeriesRange: null,
  reconciliationDashboard: null,
  personNameById: new Map(),
  myOpenSessions: [],
  sessionCashFlowEntryCountById: {},
  sessionCashFlowEntries: {},
  turnContextLoadRaw: null,
};

export const initialTreasuryFinancialAccountState: TreasuryFinancialAccountState =
  {
    account: null,
    loading: false,
    saving: false,
    deleting: false,
    error: null,
    saveError: null,
    deleteError: null,
    saveSuccess: false,
    deleteSuccess: false,
    lastAction: null,
    lastSavedAccountId: null,
  };


/**
 * GET `/CashFlowReport/GetCashFlowReport` line (PascalCase API). When the report is
 * loaded with an account scope (`FinancialAccountId` in the request), the field is
 * implicit; tenant-wide loads, however, carry the originating account id per row —
 * the OData mapper passes it through, so aggregates (`dailyTotalsByAccount`) can
 * group by it.
 */
export interface CashFlowReportLine {
  CashFlowEntryId: string;
  FinancialAccountId?: string;
  Date?: string;
  CompetenceDate?: string;
  PaymentDate?: string;
  Description?: string;
  Direction: FlowDirection;
  /**
   * Wire-fidelity enum ({@link BackendEntryType}) — preserves Sangria
   * (Withdrawal) vs Suprimento (Supply) vs Adjustment so the timeline
   * can filter / label them independently. Decoded from the OData
   * `EntryType` field via {@link decodeBackendEntryTypeFromOData}.
   */
  EntryType: BackendEntryType;
  Amount: TreasuryMoney;
  RunningBalance?: number;
  Category?: string | null;
  PaymentMethod?: CashFlowPaymentMethod | null;
  OriginType?: number;
  CashSessionId?: string | null;
  Status?: CashFlowEntryStatus;
  ReconciliationStatus?: ReconciliationStatus | null;
  MatchedBankTransactionId?: string | null;
  MatchConfidence?: number | string | null;
  MatchMethod?: string | null;
  AddedOnUtc?: string | null;
  Details?: string | null;
  CashAmount?: number | null;
  CardAmount?: number | null;
  PixAmount?: number | null;
  BankSlipAmount?: number | null;
  CheckAmount?: number | null;
  CashFlowEntryRequestId?: string | null;
  RequestDescription?: string | null;
  /** Card / acquirer transaction id (`TId` on OData `CashFlowEntries`). */
  TId?: string | null;
  CardholderName?: string | null;
  CardNumber?: string | null;
  CardBrand?: string | null;
  /** Customer of the originating request (sale/receipt). FullName resolved via PersonLookupService → `personNameById`. */
  PersonId?: string | null;
  /** Audit PersonId of the user who recorded the entry. FullName resolved via PersonLookupService → `personNameById`. */
  AddedBy?: string | null;
}

export interface TGetCashFlowReport {
  StartDate: string;
  EndDate: string;
  FinancialAccountId: string;
  FinancialAccountName: string;
  OpeningBalance: number;
  TotalInflows: number;
  TotalOutflows: number;
  ClosingBalance: number;
  Entries: CashFlowReportLine[];
  InflowsByType: Record<string, number>;
  OutflowsByType: Record<string, number>;
}

/**
 * One row returned by the cash-home charts OData query:
 * `$apply=groupby((Direction,EntryType),aggregate(Amount with sum as Total,
 *   CashAmount with sum as TotalCash, CardAmount with sum as TotalCard,
 *   PixAmount with sum as TotalPix, BankSlipAmount with sum as TotalBankSlip,
 *   CheckAmount with sum as TotalCheck, $count as Count))`.
 * Used to feed the 3 charts in the cash-turns home page.
 */
export interface CashHomeChartsAggregateRow {
  Direction: FlowDirection;
  /** Raw backend int matching {@link BackendEntryType} (0–5). */
  EntryType: number;
  Total: number;
  TotalCash: number;
  TotalCard: number;
  TotalPix: number;
  TotalBankSlip: number;
  TotalCheck: number;
  Count: number;
}

/**
 * Filters for `/OData/CashFlowEntries` chart aggregation. Mirrors the
 * cash-turns grid's header filters so the cards stay in sync with the
 * sessions the user is looking at. Session-level fields (`operatorPersonId`,
 * `sessionStatus`, `withDifference`, `activityWindow*`) are applied via the
 * `CashSession` navigation property on the flow-entry resource.
 */
export interface CashHomeChartsRequest {
  startDate?: string;
  endDate?: string;
  financialAccountId?: string;
  operatorPersonId?: string;
  sessionStatus?: CashSessionStatus;
  withDifference?: boolean;
  withoutDifference?: boolean;
  activityWindowStart?: string;
  activityWindowEnd?: string;
  financialAccountName?: string;
}

/** GET `/CashFlowReport/GetCashFlowReport` body is `CashFlowReportLine[]`; `FinancialAccountId` is the request scope. */
export interface CashFlowReportSnapshot {
  FinancialAccountId: string;
  Lines: CashFlowReportLine[];
}

/**
 * GET `/ReconciliationReport/GetDashboard` response shape.
 * Reference: `libs/domains/treasury/data/src/lib/bank-reconciliation-frontend-spec.md:1191-1208`.
 */
export interface TreasuryDashboardPayload {
  TotalTransactions: number;
  MatchedTransactions: number;
  PendingTransactions: number;
  ExceptionTransactions: number;
  MatchRate: number;
  AutoMatchRate: number;
  CompletionRate: number;
  AverageDaysToReconcile: number;
  StraightThroughProcessingRate: number;
  TotalImportedAmount: number;
  TotalMatchedAmount: number;
  TotalPendingAmount: number;
  TotalExceptionAmount: number;
}

/** Report lines and/or direct API cash-flow rows — use for aggregates without converting to {@link CashFlowEntry}. */
export type CashFlowMovement = CashFlowReportLine | CashFlowEntry;

export interface TreasuryDateRange {
  StartDate: string;
  EndDate: string;
}

export type TreasuryHomeRequest = Partial<TreasuryDateRange> & {
  FinancialAccountId?: string | null;
  BankConnectionId?: string | null;
};

/**
 * Server-side aggregates for the cash-flow summary cards on the account detail
 * page (`totalIn` / `totalOut` / `totalReconciled`). Populated by
 * `TreasuryService.getCashFlowAggregates(...)` via OData `$apply=...aggregate(...)`.
 */
export interface CashFlowAggregates {
  totalIn: TreasuryMoney;
  totalOut: TreasuryMoney;
  totalReconciled: TreasuryMoney;
}

/**
 * Scope for `TreasuryService.getCashFlowAggregates(...)`. Drives the
 * account-detail summary cards.
 *
 * `rawFilter` is the highest-priority field — when provided, it's plugged in
 * as the `$apply=filter(...)` clause verbatim. The intended source is the
 * outgoing OData `$filter` that the grid's `DataSource` just sent (captured
 * via `DxOdataStoreService`'s `exposeRequest`), so the cards stay in
 * lockstep with the grid's filters (period, direction, search, …).
 *
 * `financialAccountId` is the fallback when no raw filter is available
 * (initial mount, before the grid has issued its first load).
 *
 * `includeReconciled` toggles the *second* HTTP call that computes
 * `totalReconciled`. Cash accounts don't reconcile against a bank, so the
 * card is hidden for them and the round-trip is skipped (defaults to `true`
 * when omitted).
 */
export interface CashFlowAggregatesScope {
  financialAccountId?: string | null;
  rawFilter?: string | null;
  includeReconciled?: boolean;
}

/**
 * One day's worth of cash-flow aggregates, produced by
 * `TreasuryService.getCashFlowEntriesByDay(...)`. The day (`Day`, a
 * `YYYY-MM-DD` string) is the **local (UTC-3) day** derived from each entry's
 * `AddedOnUtc`, not the stored `CompetenceDate` column.
 *
 * - `totalAmount` is the running balance up to and including that day — the
 *   account's `InitialBalance` plus the carried-forward net of everything
 *   before the visible window plus the day's inflows (`Direction.In`) minus
 *   outflows (`Direction.Out`).
 * - `Count` is the total number of entries on that day.
 */
export interface CashFlowEntriesByDay {
  Day: string;
  totalAmount: TreasuryMoney;
  Count: number;
}

export type CashFlowEntriesByDayMap = Record<
  string,
  CashFlowEntriesByDay
>;

export interface ITreasurySettings {
  TreasurySettingsId: string | null;
  AutoSessionEnabled: boolean;
  FinancialAccountId: string | null;
  CardFinancialAccountId: string | null;
  PixFinancialAccountId: string | null;
  BankSlipFinancialAccountId: string | null;
  ToleranceType: 0 | 1;
  ToleranceValue: number | null;
  UpdatedOnUtc: string | null;
  Operation: string | null;
  ValidationResult: Record<string, unknown>;
  IsValid: boolean;
}

/**
 * POST `/CashSession/OpenSession` request body.
 *
 * **Invariant:** `FinancialAccountId` MUST point to a `FinancialAccount` whose
 * `Type === FinancialAccountType.Cash`. Sessions are exclusive to cash-type
 * accounts — non-cash channels (Pix/Card/BankSlip/Check) flow into dedicated
 * financial accounts outside any session.
 */
export interface TreasuryOpenSessionRequest {
  FinancialAccountId: string;
  OpenedBy: string;
  OpeningBalance: TreasuryMoney;
  Notes?: string;
}

export interface TreasuryCloseSessionRequest {
  CashSessionId: string;
  ClosingBalance: TreasuryMoney;
}

export interface TreasuryFinalizeSessionRequest {
  CashSessionId: string;
  ClosedBy: string;
}

export interface TreasuryApproveSessionRequest {
  CashSessionId: string;
  ApprovedBy: string;
  Notes?: string;
}

export interface TreasuryRejectSessionRequest {
  CashSessionId: string;
  RejectedBy: string;
  Notes?: string;
}

export interface TreasuryReopenSessionRequest {
  CashSessionId: string;
}

export interface TreasuryManualCashFlowEntryRequest {
  FinancialAccountId: string;
  Direction: string | number;
  EntryType: string | number;
  Amount: TreasuryMoney;
  Description: string;
  CompetenceDate: string;
  CashSessionId?: string;
  PaymentDate?: string;
  Category?: string;
  AutoConfirm?: boolean;
  /**
   * Identificador da conta-folha do plano de contas (treasury hierárquico).
   * Obrigatório para que o lançamento entre no DRE; opcional aqui para
   * preservar fluxos de tenants fora da allowlist do treasury.
   * Ver `libs/domains/treasury/CHART_OF_ACCOUNTS_API.md` § Integração.
   */
  ChartOfAccountsNodeId?: string;
}

export interface TreasuryCreateCashOperationRequest {
  CashSessionId: string;
  OperationType: string | number;
  Amount: TreasuryMoney;
  Reason: string;
  /** Logged-in user id (`UserId` from role when present, otherwise `PersonId`). */
  AuthorizedBy: string;
}

export interface TreasuryCreateCashTransferRequest {
  FromAccountId: string;
  ToAccountId: string;
  Amount: TreasuryMoney;
  TransferDate: string;
  Description?: string;
}
